import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import { listTypeApi } from '../../entities/listType/listTypeApi';
import { listTypeEntryKey } from '../../entities/listType/listTypeEntryKey';
import type { ListTypeDefinition, ListTypeEntry } from '../../entities/listType/listTypeTypes';
import { extractError } from '../../store/thunkHelpers';
import { useToast } from '../useToast';

/**
 * Il testo di una voce di picklist (FILO-16). Senza `listTypeEntry` crea una voce nuova: il testo è
 * obbligatorio, e sotto si vede la chiave che ne nascerà. Con `listTypeEntry` cambia solo il testo: la key resta.
 * Un solo campo: il server copia il testo anche nella lingua predefinita del portale.
 */
export default function ListTypeEntryDialog({
  listTypeDefinition,
  listTypeEntry,
  onClose,
  onSaved,
}: {
  listTypeDefinition: ListTypeDefinition;
  listTypeEntry?: ListTypeEntry;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [testo, setTesto] = useState(listTypeEntry?.name ?? '');
  const [salvataggio, setSalvataggio] = useState(false);

  const chiave = listTypeEntry ? listTypeEntry.key : listTypeEntryKey(testo);

  const salva = async () => {
    setSalvataggio(true);
    try {
      if (listTypeEntry) await listTypeApi.update(listTypeEntry.listTypeEntryId, { name: testo });
      else await listTypeApi.create(listTypeDefinition.listTypeDefinitionId, { name: testo });
      onSaved();
      onClose();
    } catch (e) {
      toast({ severity: 'error', message: extractError(e) });
    } finally {
      setSalvataggio(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      <DialogTitle className="tc-head">
        <span className="tc-kicker">
          {listTypeEntry ? t('settings.picklists.dialog.edit', { name: listTypeEntry.name }) : t('settings.picklists.dialog.new', { picklist: listTypeDefinition.name })}
        </span>
        <IconButton aria-label={t('common.close')} onClick={onClose} size="small" className="tc-x">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ my: 1 }}>
          <TextField
            label={t('settings.picklists.dialog.italian')}
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
            required={!listTypeEntry}
            autoFocus
            fullWidth
          />
          <span className="text-muted text-sm">
            {t('settings.picklists.dialog.key')}: <code>{chiave || '—'}</code>
          </span>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={salva} disabled={salvataggio || !testo.trim() || (!listTypeEntry && !chiave)}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
